'use client';
import React, { useEffect, useMemo, useState, type ReactNode } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import ModalTitle from '../modal-title';
import UserIcon from '../user-icon';
import { useTranslations } from 'next-intl';
import { Switch } from '@/components/ui/switch';
import clsx from 'clsx';
import { useGetUserSystemSettings, useSetUserSystemSettings } from '../../services/user';
import { LANGUAGE_ICON_MAP } from '../../constants';
import Image from 'next/image';
import { ChevronDownIcon } from 'lucide-react';
import useSwitchLanguage from '../../hooks/useSwitchLanguage';
import { useSetAtom } from 'jotai';
import { userSystemSettingsAtom } from '@/store/userinfo';
import type { SystemSettingsRes } from '@/types/api/user/system-settings.d';
import { track } from '@/utils/track';

interface ModalSettingProps {
  /** 弹窗标题 */
  title?: string;
  /** 自定义样式 */
  className?: string;
  /** 关闭弹窗时的回调函数 */
  onClose?: () => void;
  /** 编辑成功时的回调函数 */
  onSuccess?: () => void;
}
interface ModalSettingReturnType {
  result?: boolean;
}

const ListItem = ({ title, icon, rightContent, onClick }: { title: string, icon: string, rightContent: ReactNode, onClick?: () => void; }) => {
  return (
    <div className='mb-2.5 flex items-center w-full h-[55px] px-[.9375rem] rounded-[8px] bg-dark-special' onClick={onClick ? onClick : undefined}>
      <UserIcon name={icon} className='w-[18px] h-[18px] mr-2.5' />
      <p className='font-medium text-xs text-secondary'>{title}</p>
      {rightContent}
    </div>
  );
};

// Main Modal Component
const ModalSetting = NiceModal.create<ModalSettingProps>(({
  title: _title = '',
  className,
  onClose,
}) => {
  const modal = useModal();
  const t = useTranslations('personal');
  const setUserSystemSettingsAtom = useSetAtom(userSystemSettingsAtom);

  const { data: userSystemSettingsData, refetch: refetchUserSystemSettings } = useGetUserSystemSettings.useQueryHook({
    params: {},
    queryConfig: {
      enabled: false,
    }
  });
  useEffect(() => {
    if (modal.visible) {
      refetchUserSystemSettings();
    }
  }, [modal.visible, refetchUserSystemSettings]);

  // 本地状态，用于手动修改设置值
  const [localSettings, setLocalSettings] = useState<SystemSettingsRes['userSystemSettings'] | null>(null);

  // 初始化本地状态：当接口数据加载时，同步到本地状态
  useEffect(() => {
    if (userSystemSettingsData?.userSystemSettings) {
      const settings = userSystemSettingsData.userSystemSettings;
      setLocalSettings(settings);
      // 同步数据到 store
      setUserSystemSettingsAtom(settings);
    }
  }, [userSystemSettingsData, setUserSystemSettingsAtom]);

  // 当弹窗打开时，如果没有本地状态，使用默认值
  useEffect(() => {
    if (modal.visible && !localSettings && !userSystemSettingsData) {
      const defaultSettings = {
        language: 'en-US',
        musicSwitch: false,
        soundEffectSwitch: false,
      };
      setLocalSettings(defaultSettings);
    }
  }, [modal.visible, localSettings, userSystemSettingsData]);

  // 使用本地状态或接口数据
  const userSetting = useMemo(() => {
    if (localSettings) {
      return localSettings;
    }
    if (userSystemSettingsData) {
      return userSystemSettingsData.userSystemSettings;
    }
    return {
      language: 'en-US',
      musicSwitch: false,
      soundEffectSwitch: false,
    };
  }, [localSettings, userSystemSettingsData]);

  const { mutate: setUserSystemSettings } = useSetUserSystemSettings({
    mutationConfig: {
      onSuccess: (res) => {
        if (!res.code) {
          refetchUserSystemSettings();
        }
      },
    },
  });

  // 更新设置值：先更新本地状态，然后调用接口
  const updateLocalSetting = (key: string, value: boolean | string) => {
    track('profile_page_button_click', {
      current_page: 'Settings',
      button_name: key === 'musicSwitch' ? (value ? 'On Music' : 'Off Music') : (value ? 'On Effects' : 'Off Effects'),
    });
    // 先更新本地状态（立即生效）
    const newSettings = {
      ...userSetting,
      [key]: value,
    };
    setLocalSettings(newSettings);
    // 同步更新到 store，让其他地方可以立即看到变化
    setUserSystemSettingsAtom(newSettings);
    // 然后调用接口同步到服务器
    setUserSystemSettings({
      params: {
        userSystemSettings: newSettings,
      },
    });
  };
  const commonT = useTranslations('common');
  const { language, showLanguageSelect } = useSwitchLanguage({ source: 'Settings' });



  const handleClose = () => {
    onClose?.();
    modal.resolve(true);
    modal.remove();
  };



  return (
    <Drawer open={modal.visible} onOpenChange={(open) => {
      if (!open) {
        handleClose();
      }
    }} shouldScaleBackground={false}>
      <DrawerTitle></DrawerTitle>
      <DrawerContent className={clsx(
        "max-h-[50vh] bg-surface",
        className
      )}>
        <ModalTitle className='px-3' title={t('navigation.settings')} onClose={handleClose} showBack={false} />
        <div className='mt-[.9375rem] flex flex-col items-center px-3 w-full mb-5'>
          <ListItem
            title={t('settings.language.title')}
            onClick={() => {
              showLanguageSelect(false);
            }}
            icon='language'
            rightContent={(
              <div className='flex items-center ml-auto'>
                <Image src={LANGUAGE_ICON_MAP[language || 'en-US']} className='w-4 h-4 mr-[5px]' alt={language || 'en-US'} width={16} height={16} />
                <p className='font-medium text-ss text-tertiary'>{commonT(`language.${language || 'en-US'}` as any)}</p>
                <ChevronDownIcon className='size-4 opacity-50' />
                {/* <UserIcon name='more' className='ml-2.5 w-1.5 h-2.5' /> */}
              </div>
            )}
          />
          <ListItem
            title={t('settings.audio.music')}
            icon={userSetting.musicSwitch ? 'music-on' : 'music-off'}
            rightContent={
              <Switch className='h-5 ml-auto' checked={userSetting.musicSwitch} onCheckedChange={(checked) => updateLocalSetting('musicSwitch', checked)} />
            }
          />
          <ListItem
            title={t('settings.audio.effects')}
            icon={userSetting.soundEffectSwitch ? 'sound-on' : 'sound-off'}
            rightContent={<Switch className='h-5 ml-auto' checked={userSetting.soundEffectSwitch} onCheckedChange={(checked) => updateLocalSetting('soundEffectSwitch', checked)} />}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
});

ModalSetting.displayName = 'ModalSetting';

// eslint-disable-next-line react-refresh/only-export-components
export const showModalSetting: (props?: ModalSettingProps) => Promise<ModalSettingReturnType> = (props = {}) => {
  return NiceModal.show(ModalSetting, props);
};

export default ModalSetting;